import { useEffect, useRef } from 'react'

const isValidCoord = c =>
  Array.isArray(c) && c.length >= 2 && Number.isFinite(c[0]) && Number.isFinite(c[1])

const extendBbox = (bbox, coord) => {
  const [lng, lat] = coord
  bbox[0] = Math.min(bbox[0], lng)
  bbox[1] = Math.min(bbox[1], lat)
  bbox[2] = Math.max(bbox[2], lng)
  bbox[3] = Math.max(bbox[3], lat)
}

const computeBbox = geojson => {
  const bbox = [Infinity, Infinity, -Infinity, -Infinity]
  let count = 0

  const walkCoords = coords => {
    if (!coords) return
    // Point: [lng, lat]
    if (typeof coords[0] === 'number') {
      if (isValidCoord(coords)) {
        extendBbox(bbox, coords)
        count += 1
      }
      return
    }
    // Nested coords
    coords.forEach(walkCoords)
  }

  for (const f of geojson?.features || []) {
    walkCoords(f?.geometry?.coordinates)
  }

  if (!count) return null
  return bbox
}

export const useFitMapToGeojson = ({
  map,
  mapLoaded,
  layersReady,
  geojson,
  enabled,
  padding = 80,
  maxZoom = 18,
  duration = 650,
  // evita “pelearse” con navegación por treeId / click
  block = false,
}) => {
  const lastSigRef = useRef(null)

  useEffect(() => {
    if (!map || !mapLoaded || !layersReady) return
    if (!enabled || block) return
    if (!geojson?.features?.length) return

    // Firma simple para evitar re-fit por renders repetidos
    const sig = `${geojson.features.length}:${geojson.features[0]?.properties?.id ?? ''}`
    if (lastSigRef.current === sig) return
    lastSigRef.current = sig

    const bbox = computeBbox(geojson)
    if (!bbox) return

    const [[minLng, minLat], [maxLng, maxLat]] = [
      [bbox[0], bbox[1]],
      [bbox[2], bbox[3]],
    ]

    // Caso: solo 1 punto o bbox degenerado
    const isDegenerate = minLng === maxLng && minLat === maxLat

    if (isDegenerate) {
      map.easeTo({ center: [minLng, minLat], zoom: Math.min(maxZoom, 18), duration })
      return
    }

    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      {
        padding,
        duration,
        maxZoom,
      }
    )
  }, [map, mapLoaded, layersReady, geojson, enabled, padding, maxZoom, duration, block])
}
